import { useMemo } from 'react';
import { useDs } from './strings';
import { SYMBOL_DATA_URI } from './symbol-mark';

// ── Loading State cósmico — portado do sistema da referência (loading/ls-cosmos).
// Nebulosa + starfield determinístico + logo a respirar + anéis orbitais + wave bars.
const KEYFRAMES = `
@keyframes cx-twinkle{0%,100%{opacity:.2}50%{opacity:.9}}
@keyframes cx-spin{to{transform:rotate(360deg)}}
@keyframes cx-spin-r{to{transform:rotate(-360deg)}}
@keyframes cx-breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
@keyframes cx-wave{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1)}}
@keyframes cx-step-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.cx-scene *{animation:none!important}}
`;

function Stars({ count = 46 }: { count?: number }) {
  const stars = useMemo(() => {
    let s = 1337; const rng = () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
    return Array.from({ length: count }, () => ({
      x: rng() * 100, y: rng() * 100, sz: rng() < 0.72 ? 1 : rng() < 0.9 ? 1.5 : 2,
      delay: rng() * 5, dur: 2 + rng() * 3.5, op: 0.12 + rng() * 0.55,
    }));
  }, [count]);
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {stars.map((st, i) => (
        <div key={i} style={{ position: 'absolute', left: `${st.x}%`, top: `${st.y}%`, width: st.sz, height: st.sz,
          borderRadius: '50%', background: '#fff', opacity: st.op, animation: `cx-twinkle ${st.dur}s ease-in-out ${st.delay}s infinite` }} />
      ))}
    </div>
  );
}

/** Um passo do arranque — a forma que o `/loading/context` publica. */
export interface BootStep { id: string; label: string; done?: boolean }

// Os passos REAIS do backend, por baixo da legenda. Cada linha entra ao seu
// ritmo (60 ms de escalonamento) para o bloco se ler como uma sequência a
// acontecer, e não como uma lista que apareceu de repente.
//
// `aria-live="polite"`: quem ouve o ecrã recebe os passos à medida que chegam,
// em vez de um silêncio até a app abrir. O ponto NÃO é anunciado (é decoração;
// o estado já está na palavra) e o bloco é um só «status», não seis alertas.
export function BootSteps({ steps }: { steps: BootStep[] }) {
  if (steps.length === 0) return null;
  return (
    <div role="status" aria-live="polite" style={{
      display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4,
      minWidth: 240, maxWidth: '86vw',
    }}>
      {steps.map((s, i) => (
        <div key={s.id} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: 'var(--mono)', fontSize: 'var(--fs-caption)', letterSpacing: '.06em',
          color: s.done ? 'var(--fg-60)' : 'var(--fg-40)',
          opacity: 0, animation: `cx-step-in var(--t-base) ease-out ${i * 60}ms forwards`,
        }}>
          <span aria-hidden style={{
            width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
            background: s.done ? 'var(--ink-success)' : 'var(--border-3)',
            boxShadow: s.done ? '0 0 6px var(--line-success)' : 'none',
            transition: 'background var(--t-base) ease-out',
          }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

export function CosmicLoader({ caption, full = true, steps = [] }: {
  caption?: string;
  full?: boolean;
  /** Passos reais do backend (`GET /loading/context`). Vazio → só a legenda. */
  steps?: BootStep[];
}) {
  const ds = useDs();
  const text = caption ?? ds('ds.preparing', 'A preparar a tua experiência…');
  return (
    <div className="cx-scene" style={{
      position: full ? 'fixed' : 'absolute', inset: 0, zIndex: 400,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18,
      background: 'radial-gradient(ellipse 130% 90% at 15% -15%,rgba(123,97,255,0.14),transparent 55%),radial-gradient(ellipse 90% 70% at 115% 35%,rgba(93,63,211,0.09),transparent 50%),var(--bg-0)',
    }}>
      <style>{KEYFRAMES}</style>
      <Stars />
      <div style={{ position: 'relative', width: 120, height: 120, display: 'grid', placeItems: 'center' }}>
        <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', border: '1px solid rgba(167,139,250,0.12)', animation: 'cx-spin var(--t-orbit) linear infinite' }}>
          <span style={{ position: 'absolute', top: '50%', left: '50%', width: 3, height: 3, borderRadius: '50%', background: 'rgba(167,139,250,0.7)', boxShadow: '0 0 6px rgba(167,139,250,0.7)', transform: 'translateX(50px) translateY(-50%)' }} />
        </div>
        <div style={{ position: 'absolute', width: 88, height: 88, borderRadius: '50%', border: '1px solid rgba(0,209,255,0.10)', animation: 'cx-spin-r var(--t-orbit-slow) linear infinite' }} />
        {/* Símbolo embutido (data-URI) — sem caixa branca, sem rede: pinta com o
            loader numa só vez (adeus flash "fundo branco → foto do logo"). */}
        <img src={SYMBOL_DATA_URI} alt="Marcianus" decoding="sync" style={{
          width: 56, height: 56, objectFit: 'contain', zIndex: 2,
          filter: 'drop-shadow(0 0 26px rgba(123,97,255,0.55))',
          animation: 'cx-breathe var(--t-breathe) ease-in-out infinite',
        }} />
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(167,139,250,0.85)', marginBottom: 8 }}>
        Marcianus Experience
      </div>
      {/* A legenda é a promessa; os passos são a prova. Sem passos (offline, ou
          antes da 1.ª resposta), a legenda sozinha continua a ser suficiente. */}
      <div style={{ color: 'var(--fg-40)', fontSize: 12 }}>{text}</div>
      <BootSteps steps={steps} />
    </div>
  );
}
